/**
 * The agreement detail panel — shared by the expandable table row and the
 * detail route.
 *
 * Extracted 2026-07-31 when the detail route landed. Both surfaces have to show
 * the SAME ~30 fields, and two copies would drift the moment a field was added
 * to one: the row would show a value the page did not, and nobody would notice
 * because each looks complete on its own. One component, two mount points.
 *
 * Types live here too, so the row and the page cannot disagree about the payload
 * shape either — GET /api/btp/agreements and GET /api/btp/agreements/:id return
 * the same agreement object by design.
 *
 * `Field` and `Group` moved to features/crm/components/DetailPanel on 2026-08-09
 * so the Hotels and Customers panels render missing values the same way this one
 * does.
 */
import { useState } from 'react';
import { useMe } from '@/features/crm/auth/use-capabilities';
import { Field, Group } from '@/features/crm/components/DetailPanel';
import { GdsCodes } from '@/features/crm/components/GdsCodes';
import {
  AgreementEditModal,
  canEditAgreement,
} from '@/features/hotel-supplier/components/AgreementEditModal';
import {
  amenityLabel,
  amenityValueLabel,
} from '@/features/hotel-supplier/amenities';
import { RateForm } from '@/features/hotel-supplier/components/RateForm';
import { BlackoutForm } from '@/features/hotel-supplier/components/BlackoutForm';
import {
  AlternateRacs,
  type AlternateRac,
} from '@/features/hotel-supplier/components/AlternateRacs';
import { RateTestLog } from '../../components/RateTestLog';
import { SupplierContactLog } from '../../components/SupplierContactLog';
import {
  fmtCalendarDate as fmtDate,
  fmtMaybeCalendarDate as fmtMaybeDate,
  fmtMaybeInstant as fmtMaybeTimestamp,
} from '@/features/crm/lib/dates';
import { HotelContacts } from '@/features/hotel-supplier/components/HotelContacts';

export interface AgreementRate {
  id: string;
  rateType: string;
  amount: string;
  currency: string;
  commissionPct: string | null;
  availabilityType: string;
  effectiveFrom: string;
  effectiveTo: string;
  gdsLoadedStatus: string;
  season: string | null;
  /** One row per season × ROOM TYPE × extended-stay tier — all three are part
   *  of what makes a rate row distinct, so all three are shown. */
  roomType: string | null;
  rateCode: string | null;
  extendedStayTier: string | null;
  rateModel: string;
  dynamicDiscountPct: string | null;
  rateIncludesVat: boolean | null;
}

export interface AgreementLoad {
  id: string;
  target: string;
  gds: string | null;
  rac: string | null;
  routing: string;
  status: string;
  assignedTeam: string | null;
  notes: string | null;
}

export interface AgreementBlackout {
  id: string;
  startsOn: string;
  endsOn: string;
  notes: string | null;
}

/**
 * The linked `hsp.hotel_master` record, joined in by the API rather than
 * `include`d — that table takes no foreign key because it is a full-refresh
 * Domo mirror. Null is normal: it means this sourcing row was never matched to
 * the master dataset, or its canonical id went stale in a refresh.
 */
export interface HotelMaster {
  canonicalId: string;
  name: string;
  city: string | null;
  state: string | null;
  phone: string | null;
  sabrePropertyCode: string | null;
  travelportCode: string | null;
  worldspanCode: string | null;
  gdsSystemsUsed: string | null;
  brand: string | null;
}

export interface Agreement {
  id: string;
  customerId: string;
  status: string;
  ownerTeam: string | null;
  ownerEmail: string | null;
  hotelPocName: string | null;
  hotelPocEmail: string | null;
  hsmName: string | null;
  hsmEmail: string | null;
  hsmPhone: string | null;
  completedByName: string | null;
  completedByEmail: string | null;
  tmc: string | null;
  negotiationId: string | null;
  termStart: string | null;
  termEnd: string | null;
  negotiatedCurrency: string | null;
  commissionRatePct: string | null;
  airportCodes: string | null;
  estimatedAnnualRoomNights: number | null;
  avgLengthOfStayRequested: string | null;
  lowestFixedRateRequested: string | null;
  dynamicDiscountPctRequested: string | null;
  amenities: Record<string, unknown> | null;
  internalNote: string | null;
  hotelNote: string | null;
  templateUsed: string | null;
  sentAt: string | null;
  responseDueAt: string | null;
  responseAt: string | null;
  acceptedAt: string | null;
  contractBlobPath: string | null;
  rliGeneratedAt: string | null;
  rliSentAt: string | null;
  rliAcknowledgedAt: string | null;
  property: {
    propertyName: string;
    propertyCity: string | null;
    propertyState: string | null;
    vendorFamily: string | null;
    vendorCode: string | null;
    gdsPropertyCode: string | null;
  } | null;
  /**
   * The agreement this one renews, if any. Returned by both the list and detail
   * endpoints; the detail route links back to it so a renewal chain is walkable.
   */
  supersedesId: string | null;
  hotelMaster: HotelMaster | null;
  /** Enrollment is a property of the CUSTOMER, not the agreement. */
  customer: {
    btpEnabled: boolean;
    name: string | null;
    /**
     * What a rate loading instruction has to carry beyond the rate itself.
     * Stacie, 2026-08-31, on what the section should become: "it could say
     * current GDS, current TMC, IATA, a PCC, company address, company website.
     * We can fill out rate loading instructions with that."
     *
     * All four are CUSTOMER-level, and all four are the fields feedback items
     * 12 / 28 / 47 have been blocked on sourcing for weeks — which is why they
     * are optional here and why the section says so rather than rendering four
     * silent em-dashes. IATA is the AGENCY IATA/ARC number, never an airport
     * code (confirmed 2026-08-31).
     */
    iata?: string | null;
    pcc?: string | null;
    address?: string | null;
    website?: string | null;
  } | null;
  /**
   * The newest logged contact ON THIS AGREEMENT, derived rather than stored.
   * Drives the Loading worklist's owner, date and method columns, and its
   * filter/sort. Optional because cm-api does not send it yet — the local
   * harness does, so the columns read "—" against staging until it does.
   */
  lastContact?: {
    at: string;
    by: string;
    method: string;
    direction: string;
  } | null;
  /**
   * The loading sub-status. NOT a column on the model: the vocabulary Stacie
   * listed is the rate-test result set, so this is the newest audit's result,
   * falling back to the load row's status before any test exists.
   */
  loadingSubstatus?: string | null;
  /** The moves the server permits from this row's status. List endpoint. */
  allowedTransitions?: readonly string[];
  /** Days until the term ends — negative once lapsed. Derived, so the Renewal
   *  queue can sort by urgency and say "in 57 days" without date maths. */
  daysToTermEnd?: number | null;
  /**
   * The Active sub-status Stacie asked for, DERIVED server-side from the newest
   * rate audit — never stored, so it cannot be stale. `daysSinceAudit` is null
   * when the agreement has genuinely never been audited, which is a different
   * statement from "audited zero days ago".
   */
  daysSinceAudit?: number | null;
  auditDue?: boolean;
  /** True for an ACTIVE agreement inside the 120-day renewal window. */
  dueForRenewal?: boolean;
  rates: AgreementRate[];
  /** The alternate codes the hotel loaded THIS agreement under. Optional
   *  because cm-api only began returning them on 2026-09-14; an older response
   *  renders the empty state rather than crashing. */
  racs?: AlternateRac[];
  loads: AgreementLoad[];
  blackouts: AgreementBlackout[];
  /**
   * A true timestamp, not a calendar date — format with `fmtInstant`, not
   * `fmtCalendarDate`. Returned by the list and detail endpoints and already the
   * server's secondary sort key; declared here so the CSV export can carry it as
   * the "when did this last change" audit column.
   */
  updatedAt: string;
}

/* ── The expanded detail panel ─────────────────────────────────────────────── */

/**
 * Everything about one agreement that does not fit in a scannable column.
 *
 * Ordered by how the work actually runs: who/where the property is, who to talk
 * to, what was agreed, what we asked for, then the rate / load / blackout rows,
 * then free text. Groups whose child collections are empty are omitted — an
 * empty rates table is noise, whereas an empty *field* is a signal (see Field).
 */
/**
 * Statuses at or past the loading handoff. The "what we asked for" group is
 * sourcing context and is hidden from here on — see the group's own comment.
 */
const PAST_SOURCING = new Set([
  'loading',
  'active',
  'expired',
  'terminated',
  'dismissed',
  'declined',
]);

export function AgreementDetail({
  agreement: a,
  onAgreementMayHaveChanged,
}: {
  agreement: Agreement;
  /** Bubbled from the rate-audit form: a passing audit advances the agreement,
   *  and whichever surface hosts this panel needs to refetch to see it. */
  onAgreementMayHaveChanged?: () => void;
}) {
  const h = a.property;
  const hm = a.hotelMaster;
  const [editing, setEditing] = useState(false);
  /** null = closed; 'new' = add; a rate = correct that one. */
  const [rateForm, setRateForm] = useState<'new' | AgreementRate | null>(null);
  const [blackoutForm, setBlackoutForm] = useState(false);
  const { can } = useMe();
  /*
   * The edit affordance is gated on the STATUS, not on a flag — see
   * `canEditAgreement`. Once an agreement is accepted (stored as `loading`) or
   * closed, editing its terms would be rewriting a deal somebody is loading
   * against; if it ever moves back to a sourcing status the button returns on
   * its own, with nothing to reset.
   */
  const editable = canEditAgreement(a.status) && can('write:supplier');
  /*
   * Rates stay writable AFTER acceptance, unlike the agreement's own terms.
   *
   * A rate is corrected precisely when somebody is loading it — the hotel comes
   * back with a different number, or the loader finds the agreed one was
   * mistyped — so gating this on `canEditAgreement` would lock the table at the
   * moment it is most used. What it IS gated on is the agreement being alive:
   * editing the rates of a dismissed or expired deal changes nothing anyone
   * will act on.
   */
  const CLOSED = new Set(['declined', 'dismissed', 'expired', 'terminated']);
  const canEditRates = can('write:supplier') && !CLOSED.has(a.status);

  return (
    <div className="detail-panel">
      <Group label="Property" testId="agreement-detail-property">
        <dl className="detail-grid">
          {/* Grouped the way the work reads, not the way the columns happen to
              sit in the table: WHERE it is, then HOW to address it in a GDS.
              Stacie, 2026-08-31 — "hotel, brand, and chain on one line, then
              vendor codes on another line, and then GDS and airports". */}
          {/* "Hotel" IS NOT REPEATED HERE (item 69, cut 2026-09-11). The row
              above this panel carries it on every stage, and the standalone
              detail page carries it as the page's own heading — so this field
              was the property's name rendered a third time, two lines from
              where the reader clicked it.

              `Sabre ID` and `TMC` were on the same cut list and are KEPT: both
              are columns on Sourcing/Active/Closed only, so dropping them
              would have taken them off the Loading tab, the Renewal tab and
              the detail page — where the loading team, of all people, needs
              the Sabre code most. The list was written from the Sourcing
              columns alone. */}
          <Field label="Brand" value={hm?.brand} />
          <Field label="Chain family" value={h?.vendorFamily} />
          {/* CITY AND STATE, restored 2026-09-11 (item 69).
              The hotel drawer showed them and this one did not, which is what
              the duplication audit found first: two hand-rolled panels over the
              same record, diverging in CONTENT rather than only in layout. Same
              fallback the row uses — the customer-scoped sourcing record first,
              then the master, which is the authoritative source for where a
              hotel is. */}
          <Field label="City" value={h?.propertyCity ?? hm?.city} />
          <Field label="State" value={h?.propertyState ?? hm?.state} />
          {/* GBTA's AIRCITYCODE — the closest airport city code, NOT anything
              TMC-related. That ambiguity was an open question until the GBTA
              template settled it; the label now says which it is. */}
          <Field label="Closest airport(s)" mono value={a.airportCodes} />
          <Field label="Hotel phone">
            {hm?.phone ? <a href={`tel:${hm.phone}`}>{hm.phone}</a> : null}
          </Field>
          {/* Present only when the sourcing row has never been matched to the
              master dataset — which is exactly why the GDS codes above are
              blank, so it is worth saying out loud rather than leaving four
              mysterious dashes. */}
          {!hm && (
            <Field label="Master record">
              <span className="muted">
                Not linked to the hotel master — GDS codes and phone are
                unavailable until this property is matched.
              </span>
            </Field>
          )}
        </dl>
      </Group>

      {/* The GDS codes, from the component the hotel drawer uses, so the two
          panels cannot drift apart again.

          NO WORLDSPAN KEY, and that is not an omission: Stacie withdrew the
          field from this panel on 2026-08-31 — "can we remove all world span to
          everything… isn't that system dying?" — and item 69 read its absence
          as drift because the audit compared the two panels without reading
          why they differed. The spec caught the attempt to put it back. */}
      <GdsCodes
        codes={{
          sabre: hm?.sabrePropertyCode,
          travelport: hm?.travelportCode,
          gdsSystems: hm?.gdsSystemsUsed,
          vendorCode: h?.vendorCode ?? null,
        }}
        testId="agreement-detail-gds"
      />

      {/* Directly under the property's GDS codes, because they are read
          together: the property code says WHERE to load, the rate access code
          says UNDER WHAT. Shown even when empty — "none recorded" is the
          common and meaningful answer (the hotel used the customer's own
          code), and an absent group would read as the question never asked. */}
      <Group
        label="Alternate rate access codes"
        testId="agreement-detail-racs-group"
      >
        <AlternateRacs
          agreementId={String(a.id)}
          canEdit={canEditRates}
          onChanged={() => onAgreementMayHaveChanged?.()}
          racs={a.racs ?? []}
        />
      </Group>

      <Group label="Contacts" testId="agreement-detail-contacts">
        {/*
         * THE SHARED CONTACT LIST IS THE LAYOUT, not a second static copy of
         * the same people.
         *
         * This group used to lead with eight read-only fields — hotel sales
         * manager, HSM email, HSM phone, hotel contact, contact email — and
         * render `HotelContacts` underneath them. Jeff, 2026-09-09: "can we
         * replace this old Contacts section with the same layout that we have
         * on the Hotels tab?" The Hotels-tab layout was already here; it was
         * just sitting below a static block that duplicated it, and on an
         * UNMATCHED property only the static block showed, which is what he was
         * looking at.
         *
         * So the static hotel-people fields are gone WHERE THE TABLE CAN STAND
         * IN FOR THEM, and kept where it cannot — see below. The agreement's
         * own facts (who at the hotel filled the RFP in, who owns it on our
         * side) are NOT hotel contacts and stay, in their own group.
         */}
        {hm?.canonicalId ? (
          <HotelContacts
            hotelCanonicalId={hm.canonicalId}
            testId={`agreement-hotel-contacts-${a.id}`}
          />
        ) : (
          /*
           * UNMATCHED PROPERTY — the shared list has nothing to key on. Say so
           * with the reason: "no contacts" and "contacts we cannot reach from
           * here" are different facts, and the second is fixable by matching
           * the property. What the agreement itself captured is in the group
           * below either way.
           */
          <p
            className="detail-note"
            data-testid="agreement-detail-contacts-unmatched"
          >
            <span className="muted">
              This property is not matched to the hotel master, so the shared
              contact list — the one the Hotels tab and every other agreement at
              this property share — cannot be reached. Matching the property
              fills this in.
            </span>
          </p>
        )}
      </Group>

      {/*
       * NOT hotel contacts, so NOT in the group above — and kept rather than
       * dropped when that group became the shared contact table.
       *
       * "RFP completed by" is the person AT THE HOTEL who filled this
       * agreement's RFP in, which is often not the POC we sent it to. Nobody on
       * either working session knew what the field meant ("RFP completed by, I
       * don't know that that makes sense… does that mean who's filling out the
       * RFP?"); the GBTA template that arrived on 2026-08-31 settles it. It is
       * per-AGREEMENT, so it could not move into a per-hotel contact list even
       * if we wanted it to.
       *
       * "Our owner" is ours, not theirs.
       */}
      <Group label="This agreement" testId="agreement-detail-agreement-people">
        <dl className="detail-grid">
          {/*
           * THE IMPORT'S OWN PEOPLE, and they are NOT the shared contact list.
           *
           * These are agreement COLUMNS written by the Smartsheet import; the
           * table above is `hotel_contact`, filled by crm-sync from the Domo
           * Hotel Contracts feed plus hand-added rows. Different sources, so
           * not redundant — dropping these would hide the imported HSM for a
           * matched property whose Domo feed has no such person, and Stacie's
           * CSV is the fresher of the two.
           *
           * So they sit here, under a label that says whose they are, rather
           * than above the shared list where they read as a stale duplicate of
           * it. That is what "both were asked for" (AgreementsPage.render.spec)
           * still means after the Contacts group became the table.
           */}
          <Field label="Hotel sales manager" value={a.hsmName} />
          <Field label="HSM email">
            {a.hsmEmail ? (
              <a href={`mailto:${a.hsmEmail}`}>{a.hsmEmail}</a>
            ) : null}
          </Field>
          <Field label="HSM phone">
            {a.hsmPhone ? <a href={`tel:${a.hsmPhone}`}>{a.hsmPhone}</a> : null}
          </Field>
          <Field label="Hotel contact" value={a.hotelPocName} />
          <Field label="Contact email">
            {a.hotelPocEmail ? (
              <a href={`mailto:${a.hotelPocEmail}`}>{a.hotelPocEmail}</a>
            ) : null}
          </Field>
          <Field label="RFP completed by (hotel)" value={a.completedByName} />
          <Field label="Completed by email" value={a.completedByEmail} />
          <Field
            label="Our owner"
            value={
              // WHOEVER CONTACTED LAST, then the explicit owner, then the team.
              // Stacie, 2026-08-31: "the owner in theory should be a person, not
              // just the team… yes, based on last contacted, probably" — and
              // "user, it should be a user, not a team."
              //
              // The team stays as the last fallback rather than being removed:
              // an agreement nobody has contacted yet still has to say who owns
              // it, and "rate loading" is a truer answer than a blank.
              a.lastContact?.by ??
              a.ownerEmail ??
              a.ownerTeam?.replace(/_/g, ' ')
            }
          />
        </dl>
      </Group>

      <Group
        action={
          editable ? (
            <button
              className="btn btn-sm btn-outline"
              data-testid="agreement-detail-edit"
              onClick={() => setEditing(true)}
              type="button"
            >
              Edit
            </button>
          ) : null
        }
        label="Agreed terms"
        testId="agreement-detail-terms"
      >
        <dl className="detail-grid">
          <Field label="Term">
            {a.termStart || a.termEnd ? (
              <>
                {fmtMaybeDate(a.termStart) ?? '?'} —{' '}
                {fmtMaybeDate(a.termEnd) ?? '?'}
              </>
            ) : null}
          </Field>
          <Field label="Currency" value={a.negotiatedCurrency} />
          <Field label="Commission" value={pct(a.commissionRatePct)} />
          <Field label="TMC" value={a.tmc} />
          <Field label="Negotiation ID" mono value={a.negotiationId} />
          <Field label="RFP template" value={a.templateUsed} />
          <Field label="Supplier Management">
            {a.customer
              ? a.customer.btpEnabled
                ? 'Customer enrolled'
                : 'Customer not enrolled'
              : null}
          </Field>
          <Field
            label="Contract on file"
            value={a.contractBlobPath ? 'Yes' : null}
          />
          {/* "Maybe they've contacted this one recently, but it was accepted a
              month ago. That tells me we've got to get on this hotel and
              escalate." — Stacie, 2026-08-31. Kept here rather than in the ask
              group, which the Loading tab no longer renders. */}
          <Field label="Accepted" value={fmtMaybeTimestamp(a.acceptedAt)} />
        </dl>
      </Group>

      {/* What we ASKED for, kept separate from what was agreed above — the gap
          between the two is the only way to see how far a negotiation moved.
          HIDDEN once the agreement is loading or beyond: Stacie, 2026-08-31 —
          "on the loading piece, we won't need the what we asked for section…
          estimated room nights, length of stay, lowest fixed rate, dynamic
          discount". The rate loader is not negotiating; the ask is history.
          Accepted moves OUT of this group for the same reason — it is the date
          the loading queue is measured against, so it belongs with the terms. */}
      {!PAST_SOURCING.has(a.status) && (
        <Group label="What we asked for" testId="agreement-detail-ask">
          <dl className="detail-grid">
            <Field
              label="Est. annual room nights"
              value={a.estimatedAnnualRoomNights?.toLocaleString()}
            />
            <Field
              label="Avg length of stay"
              value={a.avgLengthOfStayRequested}
            />
            <Field
              label="Lowest fixed rate"
              value={a.lowestFixedRateRequested}
            />
            <Field
              label="Dynamic discount"
              value={pct(a.dynamicDiscountPctRequested)}
            />
            <Field label="RFP sent" value={fmtMaybeTimestamp(a.sentAt)} />
            <Field
              label="Response due"
              value={fmtMaybeTimestamp(a.responseDueAt)}
            />
            <Field label="Responded" value={fmtMaybeTimestamp(a.responseAt)} />
          </dl>
        </Group>
      )}

      {/*
       * SHOWN WHEN EMPTY, if the reader can add one — a deliberate exception to
       * this panel's "omit a group whose collection is empty" rule. That rule
       * exists because an empty table is noise for a READER; it stops being
       * noise when the group is the only route to the action. Until 2026-09-14
       * there was no action, and `agreement_rate` had no write path at all.
       */}
      {(a.rates.length > 0 || canEditRates) && (
        <Group
          action={
            canEditRates ? (
              <button
                className="btn btn-sm btn-outline"
                data-testid="agreement-detail-rate-add"
                onClick={() => setRateForm('new')}
                type="button"
              >
                Add a rate
              </button>
            ) : null
          }
          label={`Rates (${a.rates.length})`}
          testId="agreement-detail-rates"
        >
          <table className="detail-table">
            <thead>
              <tr>
                <th>Season</th>
                <th>Room type</th>
                <th>Tier</th>
                <th>Type</th>
                <th>Model</th>
                <th className="right">Amount</th>
                <th className="right">Commission</th>
                <th>Availability</th>
                <th>VAT</th>
                <th>Effective</th>
                {/* NOT "GDS loaded". Stacie read that as the hotel's answer to
                    GBTA's LOAD_GDS — "will you load the rates in the GDS?" —
                    and she was reading it reasonably: that IS a real GBTA field.
                    This column is OUR verification that the rate is in the GDS,
                    which is a different fact, so the header now says so. */}
                <th>Loaded (verified)</th>
                {canEditRates && (
                  <th>
                    <span className="sr-only">Edit</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {a.rates.map((r) => (
                <tr key={r.id}>
                  <td>{r.season || '—'}</td>
                  <td>{r.roomType || '—'}</td>
                  <td>{r.extendedStayTier || '—'}</td>
                  <td>{r.rateType.replace(/_/g, ' ')}</td>
                  <td>{r.rateModel}</td>
                  {/* A dynamic rate has no fixed amount — showing `amount` for
                      one would print a placeholder as if it were negotiated. */}
                  <td className="right">
                    {r.rateModel === 'dynamic'
                      ? pct(r.dynamicDiscountPct) + ' off'
                      : `${r.currency} ${Number(r.amount).toFixed(2)}`}
                  </td>
                  <td className="right">{pct(r.commissionPct)}</td>
                  <td>{r.availabilityType.replace(/_/g, ' ')}</td>
                  <td>
                    {r.rateIncludesVat === null
                      ? '—'
                      : r.rateIncludesVat
                        ? 'incl.'
                        : 'excl.'}
                  </td>
                  <td>
                    {fmtDate(r.effectiveFrom) ?? '—'} —{' '}
                    {fmtDate(r.effectiveTo) ?? '—'}
                  </td>
                  <td>{r.gdsLoadedStatus.replace(/_/g, ' ')}</td>
                  {canEditRates && (
                    <td>
                      <button
                        className="btn btn-sm"
                        data-testid={`agreement-detail-rate-edit-${r.id}`}
                        onClick={() => setRateForm(r)}
                        type="button"
                      >
                        Edit
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {a.rates.length === 0 && (
            <p
              className="empty-inline"
              data-testid="agreement-detail-rates-empty"
            >
              No rates recorded yet. Add one per season, room type and
              length-of-stay tier as the hotel confirms them.
            </p>
          )}
        </Group>
      )}

      {a.loads.length > 0 && (
        <Group
          label={`Rate loading (${a.loads.length})`}
          testId="agreement-detail-loads"
        >
          <table className="detail-table">
            <thead>
              <tr>
                <th>Target</th>
                <th>GDS</th>
                <th>RAC</th>
                <th>Status</th>
                <th>Routing</th>
                <th>Team</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {a.loads.map((l) => (
                <tr key={l.id}>
                  <td>{l.target.toUpperCase()}</td>
                  <td>{l.gds || '—'}</td>
                  <td className="mono">{l.rac || '—'}</td>
                  <td>{l.status.replace(/_/g, ' ')}</td>
                  <td>{l.routing.replace(/_/g, ' ')}</td>
                  <td>{l.assignedTeam?.replace(/_/g, ' ') || '—'}</td>
                  <td>{l.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* RATE LOADING INSTRUCTIONS — §7.
              Stacie, 2026-08-31: "it'd be better to keep the — to have it as a
              rate load instruction section… current GDS, current TMC, IATA, a
              PCC, company address, company website. We can fill out rate
              loading instructions with that."

              THE PDF IS EXPLICITLY FUTURE STATE. Smartsheet auto-generates one
              today and she flagged the generator as "maybe a future state", so
              this builds the FIELDS and the section, not the document. */}
          <div
            data-testid="agreement-detail-rli-fields"
            style={{ marginTop: 12 }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
                marginBottom: 6,
              }}
            >
              Rate loading instructions
            </div>
            <dl className="detail-grid">
              <Field label="Current GDS" value={hm?.gdsSystemsUsed} />
              <Field label="Current TMC" value={a.tmc} />
              <Field label="Agency IATA / ARC" mono value={a.customer?.iata} />
              <Field label="PCC" mono value={a.customer?.pcc} />
              <Field label="Company address" value={a.customer?.address} />
              <Field label="Company website" value={a.customer?.website} />
            </dl>
            {/* Named, not left as four em-dashes. An empty field is a signal in
                this panel, but four of them in a row read as a broken section
                rather than as a known dependency. */}
            {!a.customer?.iata &&
              !a.customer?.pcc &&
              !a.customer?.address &&
              !a.customer?.website && (
                <p
                  className="detail-note"
                  data-testid="agreement-detail-rli-blocked"
                >
                  <span className="muted">
                    The agency IATA/ARC, PCC, address and website are not on the
                    customer record yet — that is feedback items 12, 28 and 47,
                    blocked on sourcing, not a fault in this section. Everything
                    else a loading instruction needs is above.
                  </span>
                </p>
              )}
          </div>

          <dl className="detail-grid">
            <Field
              label="RLI generated"
              value={fmtMaybeTimestamp(a.rliGeneratedAt)}
            />
            <Field label="RLI sent" value={fmtMaybeTimestamp(a.rliSentAt)} />
            <Field
              label="RLI acknowledged"
              value={fmtMaybeTimestamp(a.rliAcknowledgedAt)}
            />
          </dl>
        </Group>
      )}

      {(a.blackouts.length > 0 || canEditRates) && (
        <Group
          action={
            canEditRates ? (
              <button
                className="btn btn-sm btn-outline"
                data-testid="agreement-detail-blackout-add"
                onClick={() => setBlackoutForm(true)}
                type="button"
              >
                Add dates
              </button>
            ) : null
          }
          label={`Blackout dates (${a.blackouts.length})`}
          testId="agreement-detail-blackouts"
        >
          {a.blackouts.length === 0 && (
            <p
              className="empty-inline"
              data-testid="agreement-detail-blackouts-empty"
            >
              No blackout dates recorded.
            </p>
          )}
          <ul className="detail-list">
            {a.blackouts.map((b) => (
              <li key={b.id}>
                {fmtDate(b.startsOn) ?? '—'} — {fmtDate(b.endsOn) ?? '—'}
                {b.notes ? <span className="muted"> · {b.notes}</span> : null}
              </li>
            ))}
          </ul>
        </Group>
      )}

      {a.amenities && Object.keys(a.amenities).length > 0 && (
        <Group label="Amenities & policies" testId="agreement-detail-amenities">
          <dl className="detail-grid">
            {Object.entries(a.amenities).map(([k, v]) => (
              <Field
                key={k}
                label={amenityLabel(k)}
                value={amenityValue(k, v)}
              />
            ))}
          </dl>
        </Group>
      )}

      {/* ITEM 36 — "Notes" alone does not say whose. Stacie asked where an
          optional note lives and wanted it clear these are HOTEL notes, with
          the example "does NOT offer commissionable rates" — a fact about the
          property, true for every customer. Both fields here are scoped to THIS
          agreement, so the honest fix is to say so in the label rather than
          imply a property-level home that does not exist yet. */}
      {(a.internalNote || a.hotelNote) && (
        <Group label="Agreement notes" testId="agreement-detail-notes">
          {a.hotelNote && (
            <p className="detail-note">
              <span className="detail-note-label">From the hotel</span>
              {a.hotelNote}
            </p>
          )}
          {a.internalNote && (
            <p className="detail-note">
              <span className="detail-note-label">Internal</span>
              {a.internalNote}
            </p>
          )}
        </Group>
      )}

      {/* These last two sections are ALWAYS rendered, unlike the child collections
          above which are omitted when empty — because an ABSENCE here is itself the
          answer to a question the team asks. "Has anyone checked this rate?" and
          "have we chased this hotel?" both need an answer even when the answer is
          "no record", where an empty rates table is merely noise. Each section
          distinguishes its three states rather than letting "nothing recorded" and
          "the request failed" look alike. */}
      <Group
        action={
          /*
           * "Active — upcoming audit", the sub-status Stacie asked for, shown
           * where the audits themselves are rather than as a second status
           * chip. Server-derived from the newest audit and the 30-day cadence
           * she set on 2026-09-11, so it is never stale — the alternative was a
           * nightly job and a status wrong every morning until it ran.
           */
          a.auditDue ? (
            <span
              className="badge badge-amber badge-no-dot"
              data-testid="agreement-detail-audit-due"
            >
              {a.daysSinceAudit === null
                ? 'Never audited'
                : `Audit due — ${a.daysSinceAudit} days`}
            </span>
          ) : null
        }
        label="Rate audit"
        testId="agreement-detail-rate-tests"
      >
        <RateTestLog
          agreementId={a.id}
          onAgreementMayHaveChanged={onAgreementMayHaveChanged}
          testId="agreement-rate-test-log"
        />
      </Group>

      {/* NOT `agreement-detail-contacts` — that testId belongs to the "Contacts"
          group above, which holds the HSM / hotel-POC FIELDS. Two groups sharing
          it makes `getByTestId` ambiguous and trips Playwright's strict mode. */}
      {rateForm && (
        <RateForm
          agreementCurrency={a.negotiatedCurrency}
          agreementId={String(a.id)}
          onClose={() => setRateForm(null)}
          onSaved={() => {
            setRateForm(null);
            // Refetch rather than splice: the API normalises decimals and
            // dates, so what is on screen after a save is what was stored.
            onAgreementMayHaveChanged?.();
          }}
          rate={rateForm === 'new' ? null : rateForm}
          termEnd={a.termEnd}
          termStart={a.termStart}
        />
      )}

      {blackoutForm && (
        <BlackoutForm
          agreementId={String(a.id)}
          onClose={() => setBlackoutForm(false)}
          onSaved={() => {
            setBlackoutForm(false);
            onAgreementMayHaveChanged?.();
          }}
        />
      )}

      {editing && (
        <AgreementEditModal
          agreement={a}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            // The drawer re-reads rather than patching its own copy: the API
            // normalises decimals and dates, so our strings are not what was
            // stored.
            onAgreementMayHaveChanged?.();
          }}
        />
      )}

      <Group label="Contact history" testId="agreement-detail-contact-log">
        {/* No `agreements` — the reason picker would be asking which agreement
            this is, on the agreement's own panel. `propertyCanonicalId` IS
            passed, because the people who can be named on the call are the
            property's, not the agreement's. */}
        <SupplierContactLog
          agreementId={a.id}
          /*
           * The SAME callback the rate audit already uses. A logged contact
           * moves this agreement's last-contact columns, and the row that hosts
           * this panel renders them — so the host reloads for a contact for
           * exactly the reason it reloads for a passing audit.
           */
          onContactSaved={onAgreementMayHaveChanged}
          propertyCanonicalId={hm?.canonicalId}
          testId="agreement-contact-log"
        />
      </Group>
    </div>
  );
}

/* ── Formatting ───────────────────────────────────────────────────────────── */

/**
 * Every date in this table (`term_end`, rate `effective_to`, blackout windows) is
 * a Postgres DATE; the RLI/negotiation stamps are true timestamps. The two are
 * formatted differently and the reason is documented once, in crm/lib/dates.
 *
 * Re-exported under these names because the agreements pages and their specs
 * already import them from here — one implementation, two names that read
 * naturally at the call sites in this file.
 */
export { fmtDate, fmtMaybeTimestamp };

/** Percentages arrive as Decimal strings so no precision is lost in transit. */
function pct(value: string | null): string | null {
  return value === null ? null : `${Number(value)}%`;
}

/**
 * The amenities blob is free-form jsonb owned by the RFP template, so a value
 * can be a coded answer, a boolean, a fee amount, or a sentence. Render each
 * honestly instead of assuming a shape — and never `[object Object]`.
 *
 * `amenityValueLabel` is asked FIRST, so a coded answer reads as the words the
 * hotel gave rather than as its storage code: the breakfast question stores
 * 'B', and a drawer showing "Breakfast type: B" is a reference panel that has
 * to be decoded to be used. The local fallbacks below still cover the shapes
 * the question list knows nothing about — arrays, objects and the booleans
 * written before it existed.
 *
 * The KEY side is `amenityLabel`, which names a current question and humanises
 * a retired one, so answers stored before the 2026-09-10 question set still
 * render with a readable label rather than snake_case.
 */
function amenityValue(key: string, value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string' || typeof value === 'number') {
    return amenityValueLabel(key, value);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value))
    return value.map((v) => amenityValue(key, v) ?? '—').join(', ');
  return JSON.stringify(value);
}
